import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import Card from "../ui/Card";
import Button from "../ui/Button";

type DriverSidebarProps = {
  collapsed: boolean;
};

type DriverSidebarItem = {
  label: string;
  to: string;
  icon: string;
  end?: boolean;
};

type StoredDriverProfile = {
  photoDataUrl?: string;
  fullName?: string;
};

const driverSidebarItems: DriverSidebarItem[] = [
  {
    label: "Inicio",
    to: "/driver",
    icon: "◫",
    end: true,
  },
  {
    label: "Mis envíos",
    to: "/driver/deliveries",
    icon: "≣",
  },
  {
    label: "Mis colectas",
    to: "/driver/pickups",
    icon: "◎",
  },
  {
    label: "Mi perfil",
    to: "/driver/profile",
    icon: "◌",
  },
];

const sidebarStyle = (collapsed: boolean): React.CSSProperties => ({
  width: collapsed ? "78px" : "244px",
  minWidth: collapsed ? "78px" : "244px",
  height: "100vh",
  position: "sticky",
  top: 0,
  borderRight: "1px solid var(--border)",
  background: "var(--surface)",
  backdropFilter: "var(--glass)",
  WebkitBackdropFilter: "var(--glass)",
  boxShadow: "var(--shadow)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  transition: "width var(--transition-base), min-width var(--transition-base)",
  zIndex: 30,
});

const topSectionStyle: React.CSSProperties = {
  padding: "16px 12px 10px 12px",
};

const brandRowStyle = (collapsed: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "flex-start",
  gap: "12px",
  minHeight: "48px",
});

const logoBoxStyle: React.CSSProperties = {
  width: "42px",
  height: "42px",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--primary-soft)",
  border: "1px solid var(--border)",
  color: "var(--primary)",
  fontWeight: 800,
  fontSize: "16px",
  letterSpacing: "0.06em",
  flexShrink: 0,
  overflow: "hidden",
};

const logoImageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const brandMetaStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const brandEyebrowStyle: React.CSSProperties = {
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--text-secondary)",
};

const brandNameStyle: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const navStyle: React.CSSProperties = {
  padding: "8px 8px 0 8px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const getItemBaseStyle = ({
  active,
  collapsed,
}: {
  active: boolean;
  collapsed: boolean;
}): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "space-between",
  gap: "10px",
  minHeight: "46px",
  padding: collapsed ? "0" : "0 14px",
  borderRadius: "18px",
  textDecoration: "none",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  background: active ? "rgba(198,165,106,0.10)" : "transparent",
  border: active ? "1px solid var(--border)" : "1px solid transparent",
  transition: "all var(--transition-base)",
  fontWeight: active ? 700 : 600,
  fontSize: "14px",
});

const leftRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  minWidth: 0,
};

const iconWrapStyle: React.CSSProperties = {
  width: "18px",
  textAlign: "center",
  fontSize: "15px",
  flexShrink: 0,
};

const labelStyle: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const bottomSectionStyle: React.CSSProperties = {
  padding: "12px 8px 14px 8px",
};

const helperCardTitleStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 800,
  color: "var(--text-primary)",
  marginBottom: "6px",
};

const helperCardTextStyle: React.CSSProperties = {
  fontSize: "12px",
  lineHeight: 1.45,
  color: "var(--text-secondary)",
  marginBottom: "12px",
};

const compactInfoStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const compactChipStyle: React.CSSProperties = {
  minHeight: "34px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-secondary)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "11px",
  fontWeight: 700,
  padding: "0 8px",
};

const getInitials = (username: string, fullName?: string): string => {
  const source = (fullName || username || "").trim();
  if (!source) return "DR";

  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const DriverSidebar = ({ collapsed }: DriverSidebarProps) => {
  const username = localStorage.getItem("username") || "driver";

  const storedProfile = useMemo<StoredDriverProfile>(() => {
    try {
      const raw = localStorage.getItem(`driver_profile_${username}`);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, [username]);

  const profileImage = storedProfile?.photoDataUrl || "";
  const initials = getInitials(username, storedProfile?.fullName);

  return (
    <aside style={sidebarStyle(collapsed)}>
      <div>
        <div style={topSectionStyle}>
          <div style={brandRowStyle(collapsed)}>
            <div style={logoBoxStyle}>
              {profileImage ? (
                <img src={profileImage} alt="Driver" style={logoImageStyle} />
              ) : (
                initials
              )}
            </div>

            {!collapsed ? (
              <div style={brandMetaStyle}>
                <span style={brandEyebrowStyle}>Área operativa</span>
                <span style={brandNameStyle}>Driver</span>
              </div>
            ) : null}
          </div>
        </div>

        <nav style={navStyle}>
          {driverSidebarItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              style={{ textDecoration: "none" }}
              title={collapsed ? item.label : undefined}
            >
              {({ isActive }) => (
                <div style={getItemBaseStyle({ active: isActive, collapsed })}>
                  <div style={leftRowStyle}>
                    <span style={iconWrapStyle}>{item.icon}</span>
                    {!collapsed ? <span style={labelStyle}>{item.label}</span> : null}
                  </div>

                  {!collapsed && isActive ? <span>•</span> : null}
                </div>
              )}
            </NavLink>
          ))}
        </nav>
      </div>

      <div style={bottomSectionStyle}>
        {collapsed ? (
          <div style={compactInfoStyle}>
            <div style={compactChipStyle}>DR</div>
            <div style={compactChipStyle}>MVP</div>
          </div>
        ) : (
          <Card style={{ padding: "16px" }}>
            <div style={helperCardTitleStyle}>Panel driver</div>

            <div style={helperCardTextStyle}>
              Accedé a tus envíos y descargá el detalle disponible en legacy.
            </div>

            <Button
              variant="secondary"
              fullWidth
              onClick={() => {
                window.location.href = "/driver/deliveries";
              }}
            >
              Ver envíos
            </Button>
          </Card>
        )}
      </div>
    </aside>
  );
};

export default DriverSidebar;